import React, { useState, useEffect } from 'react';
import { Box, Container, Card, Button, Typography, CircularProgress, Grid, Chip, Divider, Paper, Snackbar, Alert } from '@mui/material';
import { donationService } from '../services/api';
import { ArrowBack, Print, FileDownload } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

const DonationDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [donation, setDonation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [receiptBusy, setReceiptBusy] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });

  useEffect(() => {
    fetchDonationDetails();
  }, [id]);

  const fetchDonationDetails = async () => {
    try {
      setLoading(true);
      const res = await donationService.getDonationById(id);
      const donationData = res.data?.data ?? res.data ?? null;
      setDonation(donationData);
    } catch (error) {
      console.error('Error fetching donation:', error);
      setDonation(null);
    } finally {
      setLoading(false);
    }
  };

  const receiptErrorMessage = async (error) => {
    const data = error.response?.data;
    if (data instanceof Blob) {
      try {
        const json = JSON.parse(await data.text());
        return json.error || json.message || 'Failed to load receipt';
      } catch (_) {
        return 'Failed to load receipt';
      }
    }
    return error.response?.data?.error || error.message || 'Failed to load receipt';
  };

  const loadReceiptBlob = async () => {
    const res = await donationService.getReceiptPdf(donation.id);
    const type = String(res.headers['content-type'] || '');
    if (type.includes('application/json')) {
      const text = await res.data.text();
      const json = JSON.parse(text);
      throw new Error(json.error || json.message || 'Failed to load receipt');
    }
    return new Blob([res.data], { type: 'application/pdf' });
  };

  const handlePrintReceipt = async () => {
    if (!donation?.id) return;
    const printWindow = window.open('', '_blank');
    try {
      setReceiptBusy(true);
      const blob = await loadReceiptBlob();
      const url = URL.createObjectURL(blob);
      if (printWindow) {
        printWindow.location = url;
        const tryPrint = () => {
          try {
            printWindow.focus();
            printWindow.print();
          } catch (_) {}
        };
        printWindow.onload = tryPrint;
        setTimeout(tryPrint, 1200);
        setTimeout(() => URL.revokeObjectURL(url), 120000);
        return;
      }
      const iframe = document.createElement('iframe');
      iframe.setAttribute('title', 'Print receipt');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.src = url;
      document.body.appendChild(iframe);
      const cleanup = () => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        URL.revokeObjectURL(url);
      };
      const tryPrint = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (_) {}
      };
      iframe.onload = tryPrint;
      setTimeout(tryPrint, 1200);
      setTimeout(cleanup, 60000);
    } catch (error) {
      if (printWindow && !printWindow.closed) printWindow.close();
      setToast({ open: true, message: await receiptErrorMessage(error), type: 'error' });
    } finally {
      setReceiptBusy(false);
    }
  };

  const handleDownloadReceipt = async () => {
    if (!donation?.id) return;
    try {
      setReceiptBusy(true);
      const blob = await loadReceiptBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `80G-receipt-${donation.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (error) {
      setToast({ open: true, message: await receiptErrorMessage(error), type: 'error' });
    } finally {
      setReceiptBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!donation) {
    return (
      <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 4 }}>
        <Container maxWidth={false}>
          <Button 
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/donations')}
            sx={{ color: '#2c5f2d', mb: 4 }}
          >
            Back to Donations
          </Button>
          <Typography variant="h5" color="error">Donation not found</Typography>
        </Container>
      </Box>
    );
  }

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      case 'refunded': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 4 }}>
      <Container maxWidth={false}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button 
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/donations')}
            sx={{ color: '#2c5f2d', mb: 2, textTransform: 'none' }}
          >
            Back to Donations
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 1 }}>
                Donation #{donation.id}
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085' }}>
                Received on {new Date(donation.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="contained"
                startIcon={receiptBusy ? <CircularProgress size={16} color="inherit" /> : <Print />}
                onClick={handlePrintReceipt}
                disabled={receiptBusy || String(donation.payment_status || '').toLowerCase() !== 'completed'}
                sx={{ backgroundColor: '#2c5f2d', '&:hover': { backgroundColor: '#1f4620' } }}
              >
                Print Receipt
              </Button>
              <Button 
                variant="outlined"
                startIcon={receiptBusy ? <CircularProgress size={16} color="inherit" /> : <FileDownload />}
                onClick={handleDownloadReceipt}
                disabled={receiptBusy || String(donation.payment_status || '').toLowerCase() !== 'completed'}
                sx={{ borderColor: '#2c5f2d', color: '#2c5f2d', '&:hover': { backgroundColor: '#f0f9ff' } }}
              >
                Download Receipt
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Main Content Grid */}
        <Grid container spacing={3}>
          {/* Left Column - Main Details */}
          <Grid item xs={12} md={8}>
            {/* Donation Amount Card */}
            <Card sx={{ p: 4, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    DONATION AMOUNT
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#059669', mt: 1 }}>
                    ₹{donation.amount.toLocaleString()}
                  </Typography>
                </Box>
                <Chip 
                  label={donation.payment_status.toUpperCase()} 
                  color={getStatusColor(donation.payment_status)}
                  sx={{ height: '40px', fontSize: '0.9rem', fontWeight: '600' }}
                />
              </Box>
              <Divider />
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                  TRANSACTION ID
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5, fontFamily: 'monospace' }}>
                  {donation.transaction_id || 'Not available'}
                </Typography>
              </Box>
            </Card>

            {/* Donor Information */}
            <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                Donor Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                      DONOR NAME
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                      {donation.name}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                      EMAIL
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5, wordBreak: 'break-all' }}>
                      {donation.email}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                      PHONE
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                      {donation.phone || 'Not provided'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                      ADDRESS
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {[donation.address, donation.city, donation.state, donation.zipcode].filter(Boolean).join(', ') || 'Not provided'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                      PAN
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                      {donation.pan_number || 'Not provided'}
                    </Typography>
                  </Box>
                </Grid>
                {donation.date_of_birth && (
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                        DATE OF BIRTH
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                        {String(donation.date_of_birth).slice(0, 10)}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Card>

            {/* Campaign Information */}
            <Card sx={{ p: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                Campaign Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                      CAMPAIGN
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                      {donation.campaign_title || 'General Donation'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                      CAMPAIGN ID
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                      {donation.campaign_id || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                {(donation.sponsor_tier_name || donation.sponsor_children_count != null) && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                          SPONSOR TIER
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                          {donation.sponsor_tier_name || '—'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                          CHILDREN (COUNT)
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                          {donation.sponsor_children_count != null ? donation.sponsor_children_count : '—'}
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                )}
                {donation.notes && (
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                        NOTES
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                        {donation.notes}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Card>
          </Grid>

          {/* Right Column - Payment & Additional Details */}
          <Grid item xs={12} md={4}>
            {/* Payment Details */}
            <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                Payment Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    PAYMENT METHOD
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                    {donation.payment_method.charAt(0).toUpperCase() + donation.payment_method.slice(1)}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    STATUS
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip 
                      label={donation.payment_status.toUpperCase()}
                      color={getStatusColor(donation.payment_status)}
                      size="small"
                      sx={{ fontWeight: '600' }}
                    />
                  </Box>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    DATE
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                    {new Date(donation.created_at).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* Recurring Donation Info */}
            {donation.is_recurring && (
              <Card sx={{ p: 3, mb: 3, border: '2px solid #7c3aed', backgroundColor: '#f5f3ff', boxShadow: '0 4px 12px rgba(124, 58, 237, 0.1)' }}>
                <Typography variant="h6" sx={{ fontWeight: '600', color: '#7c3aed', mb: 2 }}>
                  Recurring Donation
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                      FREQUENCY
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                      {donation.recurring_frequency ? donation.recurring_frequency.charAt(0).toUpperCase() + donation.recurring_frequency.slice(1) : 'N/A'}
                    </Typography>
                  </Box>
                  {donation.subscription_status && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                          SUBSCRIPTION STATUS
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5, textTransform: 'capitalize' }}>
                          {donation.subscription_status}
                        </Typography>
                      </Box>
                    </>
                  )}
                  {donation.razorpay_subscription_id && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                          RAZORPAY SUBSCRIPTION ID
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5, wordBreak: 'break-all' }}>
                          {donation.razorpay_subscription_id}
                        </Typography>
                      </Box>
                    </>
                  )}
                  {donation.razorpay_plan_id && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                          RAZORPAY PLAN ID
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5, wordBreak: 'break-all' }}>
                          {donation.razorpay_plan_id}
                        </Typography>
                      </Box>
                    </>
                  )}
                  {donation.recurring_end_date && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                          END DATE
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                          {new Date(donation.recurring_end_date).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Card>
            )}

            {/* 80G Tax Receipt Information */}
            <Card sx={{ p: 3, mb: 3, border: '2px solid #059669', backgroundColor: '#f0fdf4', boxShadow: '0 4px 12px rgba(5, 150, 105, 0.1)' }}>
              <Typography variant="h6" sx={{ fontWeight: '600', color: '#059669', mb: 2 }}>
                80G Tax Receipt
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    RECEIPT NUMBER
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5, fontFamily: 'monospace', fontWeight: '600' }}>
                    RW-80G-2026-{String(donation.id).padStart(3, '0')}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    80G REFERENCE NUMBER
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5, fontFamily: 'monospace' }}>
                    12AABCU1234H1Z0
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    TAX DEDUCTION
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                    50% deduction under Section 80G(5c)
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    VALID PERIOD
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a202c', mt: 0.5 }}>
                    01-01-2024 to 31-12-2026
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" sx={{ color: '#667085', fontWeight: '600' }}>
                    RECEIPT STATUS
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip 
                      label="Original Receipt"
                      color="success"
                      size="small"
                      sx={{ fontWeight: '600' }}
                    />
                  </Box>
                </Box>
              </Box>
            </Card>

            {/* Additional Notes */}
            {donation.notes && (
              <Card sx={{ p: 3, border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <Typography variant="h6" sx={{ fontWeight: '600', color: '#1a202c', mb: 2 }}>
                  Notes
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: '#f8fafb', borderLeft: '4px solid #2c5f2d' }}>
                  <Typography variant="body2" sx={{ color: '#667085', lineHeight: 1.6 }}>
                    {donation.notes}
                  </Typography>
                </Paper>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>
      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast({ ...toast, open: false })}>
        <Alert severity={toast.type} onClose={() => setToast({ ...toast, open: false })}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DonationDetailPage;
